import { Appearance, Box, Button, Heading, Icon, Table, Text } from "@blasterjs/core";
import React from "react";
import styled from "styled-components";
import { Image, ImageAndQuery } from "../models";

import ImageDialog from "../components/ImageDialog";
import { copyText } from "../components/UnifiedAnnotationTree";
import Timestamp from "./Timestamp";
import { useAppDispatch, useAppSelector } from "../hooks";
import { openImageDialog } from "../slices/imageDialog";
import { RolePermissions } from "../permissions";
import { formatImageStatus } from "../components/ImageManagementImageResults";
import { toggleImageForCR } from "../slices/caseImageViewer";
import TooltipFlyout, { TooltipLink, TooltipLinkDanger } from "./Tooltip";
import DownloadImageDialog from "./DownloadImageDialog";
import { openDownloadImageDialog } from "../slices/downloadImageDialog";
import ImageManagementImageDialogParent from "./ImageManagementImageDialogParent";
import {
  ImageRowOptions,
  openImageManagementImageDialogParent
} from "../slices/ImageManagementImageDialogParent";

const CompactTable = styled(Table)`
  width: 100%;
  td:first-child {
      font-weight: 700 !important;
      color: #231F20;
      font-size: 16px
      font-weight: 600;
      font-family: 
      padding-right: 1rem;
      width: 12rem;
      word-break: keep-all;
  }
  td:nth-child(2) {
    font-weight: 400;
    color: #231F20;
    font-size: 16px
    padding-right: 1rem;
    width: 12rem;
    padding-left: 2rem;
    word-break: break-word;
  }
`;

const SelectionBox = styled(Box)`
  display: flex;
  align-items: flex-start;
`;

interface Props {
  readonly studyName: string | null;
  readonly imageAndQuery: ImageAndQuery;
  readonly collapseIcon?: React.ReactNode;
  readonly inQueryChain: boolean;
}

const ImageInfo = ({ studyName, imageAndQuery, collapseIcon, inQueryChain }: Props) => {
  const dispatch = useAppDispatch();

  const image: Image = imageAndQuery.image;

  const loggedInUser = useAppSelector(state => state.auth.loggedInUser);

  const canEdit =
    loggedInUser &&
    "resource" in loggedInUser &&
    loggedInUser.resource.can([
      RolePermissions.AP_ImageData_EditAccessionNumber,
      RolePermissions.AP_ImageData_EditFileName,
      RolePermissions.AP_ImageData_EditAnatomicalSegment
    ]);

  const userCanDeleteImages =
    "resource" in loggedInUser &&
    loggedInUser.resource.can([RolePermissions.IM_ImagesListTab_Archive]);

  const userCanDownloadImages =
    "resource" in loggedInUser &&
    loggedInUser.resource.can([RolePermissions.IM_ImagesListTab_DownloadImages]);

  const downloadImageDialog = userCanDownloadImages ? <DownloadImageDialog /> : null;

  const editIcon = canEdit && (
    <Button data-tooltip={true} appearance={Appearance.MINIMAL}>
      <Icon name="menu" />
      <TooltipFlyout placement={"left-start"}>
        <TooltipLink
          onClick={() =>
            dispatch(openImageDialog({ image: image, refreshSingleImageOnSuccess: true }))
          }
        >
          Edit Image Data
        </TooltipLink>
        {userCanDownloadImages && (
          <TooltipLink onClick={() => dispatch(openDownloadImageDialog(image))}>
            Download Image
          </TooltipLink>
        )}
        {userCanDeleteImages && (
          <TooltipLinkDanger
            onClick={() =>
              dispatch(
                openImageManagementImageDialogParent({
                  imageRowOptions: ImageRowOptions.Archive,
                  imageId: image.id,
                  imageFilename: image.name,
                  studyId: image.studyId
                })
              )
            }
          >
            Archive Image
          </TooltipLinkDanger>
        )}
      </TooltipFlyout>
    </Button>
  );

  const imageDialog = canEdit ? <ImageDialog /> : null;

  const toggleImageToReaderOnClick = () => {
    dispatch(toggleImageForCR(image.id));
  };

  const showToReaderButton =
    "resource" in loggedInUser &&
    loggedInUser.resource.can([RolePermissions.AP_CaseViewer_ViewButtonPassISCQC]) &&
    inQueryChain ? (
      <Box>
        <SelectionBox>
          <Icon name="image" color="gray" />
          Show this image to reader? {image.ignored}
        </SelectionBox>
        <Text>
          {imageAndQuery.query === null
            ? "This image was uploaded as a replacement for an image with a query"
            : "This image has a query but can still be shown to readers"}
        </Text>
        {image.ignored ? (
          <Button
            mt={1}
            block={true}
            onClick={toggleImageToReaderOnClick}
            title={"Using this image will make it visible to readers"}
            appearance="prominent"
            intent="primary"
          >
            Show to Reader
          </Button>
        ) : (
          <Button
            mt={1}
            block={true}
            onClick={toggleImageToReaderOnClick}
            title={"Hiding this image will make it invisible to readers"}
            appearance="prominent"
            intent="primary"
          >
            Hide from Reader
          </Button>
        )}
      </Box>
    ) : null;

  return (
    <>
      <Box display="flex" width="100%">
        <Heading textAlign="left" mb={2}>
          <div style={{ color: "#231F20", fontWeight: "700", fontSize: "20px", height: "30px" }}>
            Image
          </div>
        </Heading>
        <Box ml={"auto"}>
          {editIcon}
          {collapseIcon}
        </Box>
      </Box>
      <CompactTable compact={true} className="image-info-table">
        <tbody>
          <tr>
            <th>Accession Number</th>
          </tr>
          <tr>
            <td>
              {image.accessionNumber ? (
                <Box
                  className="copyableBox"
                  title="click to copy"
                  onClick={(e: MouseEvent) =>
                    e && e.target && copyText((e.target as HTMLSpanElement).innerText)
                  }
                >
                  {image.accessionNumber}
                </Box>
              ) : (
                "—"
              )}
            </td>
          </tr>
          <tr>
            <th>File Name</th>
          </tr>
          <tr>
            <td>{image.name ? <Box>{image.name}</Box> : "—"}</td>
          </tr>
          <tr>
            <th>Upload Date</th>
          </tr>
          <tr>
            <td>
              {image.uploadedAt ? (
                <Box>
                  <Timestamp date={image.uploadedAt} />
                </Box>
              ) : (
                "—"
              )}
            </td>
          </tr>
          <tr>
            <th>Anatomical Segment</th>
          </tr>
          <tr>
            <td>{image.biopsyLocation ? <Box>{image.biopsyLocation}</Box> : "—"}</td>
          </tr>
          <tr>
            <th>Image Status</th>
          </tr>
          <tr>
            <td>{image.status ? <Box>{formatImageStatus(image.status)}</Box> : "—"}</td>
          </tr>
          {studyName && (
            <tr>
              <th>Study Name</th>
            </tr>
          )}
          {studyName && (
            <tr>
              <td>
                <Box>{studyName}</Box>
              </td>
            </tr>
          )}
        </tbody>
      </CompactTable>
      {showToReaderButton}
      {imageDialog}
      {downloadImageDialog}
      <ImageManagementImageDialogParent />
    </>
  );
};

export default ImageInfo;
